/**
 * Textos legales del storefront (cards #472/#473). Son los `texts` del centro
 * legal ({@link StorefrontLegalConfig}).
 *
 * Base: las plantillas consolidadas por la auditoría de tienda
 * (`agentstack-informe/plans/s8/auditoria-tienda/plantillas/`). Los datos de
 * IDENTIDAD (razón social, CUIT, domicilio, email, teléfono, horario, área) NO
 * viven acá: se referencian con tokens `{campo}` que autocompletan de un único
 * origen ({@link fillIdentityTokens} sobre la identidad del centro legal). Los
 * corchetes que quedan son placeholders de CONTENIDO no-identitario (operador
 * logístico, hosting, plazos, notas de abogado): los completa la empresa/abogado
 * y se renderizan resaltados (ver {@link placeholderSegments} + banner de borrador).
 *
 * Base normativa: Ley 24.240, CCCN (contratos de consumo a distancia), Ley
 * 25.326, Disposición 954/2025 y Disposición 3/2026.
 */

/** Un bloque de contenido dentro de un documento legal. */
export type LegalBlock =
  | { readonly kind: 'p'; readonly text: string }
  | { readonly kind: 'h2'; readonly text: string }
  | { readonly kind: 'ul'; readonly items: readonly string[] }
  | { readonly kind: 'ol'; readonly items: readonly string[] };

/** Un documento legal completo, listo para renderizar por {@link LegalPageComponent}. */
export interface LegalDoc {
  /** Slug de la ruta, sin barra (ej. `terminos`). */
  readonly slug: string;
  /** Título visible y `<h1>` de la página. */
  readonly title: string;
  /** Descripción para SEO. */
  readonly seoDescription: string;
  /** Bajada opcional bajo el título. */
  readonly intro?: string;
  /** Cuerpo del documento. */
  readonly blocks: readonly LegalBlock[];
  /** Lista "a validar por abogado/a" que cierra cada plantilla. */
  readonly lawyerNotes: readonly string[];
}

/**
 * Parte un texto en segmentos, marcando los `[PLACEHOLDER]` para resaltarlos.
 * Evita `innerHTML`: el template itera los segmentos y aplica la clase al que
 * corresponde. Exportada para testear el parseo por separado.
 * @param text Texto que puede contener `[CORCHETES]`.
 * @returns Segmentos en orden; `placeholder: true` en los `[...]`.
 */
export function placeholderSegments(text: string): readonly { text: string; placeholder: boolean }[] {
  return text
    .split(/(\[[^\]]+\])/g)
    .filter((part) => part !== '')
    .map((part) => ({ text: part, placeholder: /^\[[^\]]+\]$/.test(part) }));
}

const TERMINOS: LegalDoc = {
  slug: 'terminos',
  title: 'Términos y Condiciones de Uso y Compra',
  seoDescription:
    'Términos y condiciones de uso y compra de la tienda: objeto, precios, proceso de compra, envíos, garantía legal y derecho de arrepentimiento.',
  intro:
    'Bienvenido/a a {siteName}, titularidad de {businessName}, CUIT {taxId}, con domicilio en {address}, República Argentina, correo electrónico {email} y teléfono/WhatsApp {phone}. El uso del sitio {domain} y la compra de productos a través de él implican la aceptación de los presentes Términos y Condiciones, de la Política de Privacidad y de la Política de Cambios, Devoluciones y Arrepentimiento, disponibles de manera permanente para su consulta.',
  blocks: [
    { kind: 'h2', text: 'Objeto' },
    {
      kind: 'p',
      text: 'Estos Términos y Condiciones regulan el acceso, navegación y uso del sitio, así como la compra de productos físicos ofrecidos por {businessName} a consumidores finales dentro de la República Argentina. Las ofertas publicadas tienen validez exclusiva para el territorio nacional.',
    },
    { kind: 'h2', text: 'Capacidad' },
    {
      kind: 'p',
      text: 'Podrán comprar personas humanas mayores de edad con capacidad legal para contratar y personas jurídicas debidamente representadas. Los menores de edad podrán operar únicamente a través de sus representantes legales. Queda prohibida la utilización del sitio con fines de reventa, salvo autorización expresa y por escrito de {businessName}.',
    },
    { kind: 'h2', text: 'Productos y disponibilidad' },
    {
      kind: 'p',
      text: '{businessName} brinda información veraz, detallada y clara sobre las características de los productos ofrecidos (art. 4, Ley 24.240). Las imágenes, descripciones y especificaciones son ilustrativas y podrán presentar mínimas variaciones no esenciales. Todas las compras están sujetas a disponibilidad de stock al momento de la confirmación operativa de la orden. En caso de falta de stock o imposibilidad sobreviniente de entrega, {businessName} informará la situación al cliente y ofrecerá, según corresponda, la devolución íntegra de las sumas abonadas o una alternativa equivalente, sin perjuicio de los derechos que le asisten como consumidor.',
    },
    { kind: 'h2', text: 'Precios' },
    {
      kind: 'p',
      text: 'Todos los precios se exhiben en pesos argentinos e incluyen IVA y demás conceptos aplicables al consumidor final: el precio publicado es el importe total y final a abonar por el producto. Cuando corresponda legalmente, se exhibirá además el precio neto con la leyenda "PRECIO SIN IMPUESTOS NACIONALES". Si se ofrecieran pagos en cuotas, se informará el precio de contado, la cantidad y monto de las cuotas y el costo financiero total efectivo anual. Los costos de envío se calculan y exhiben por separado durante el proceso de compra, antes del pago.',
    },
    { kind: 'h2', text: 'Proceso de compra' },
    {
      kind: 'p',
      text: 'Para realizar una compra, el usuario deberá seleccionar los productos, incorporarlos al carrito, completar los datos requeridos y elegir el medio de pago y entrega. La confirmación del pedido enviada por correo electrónico constituye constancia de recepción de la solicitud. La aceptación de la compra se perfecciona con la validación operativa de {businessName} y el envío de la correspondiente confirmación.',
    },
    { kind: 'h2', text: 'Medios de pago' },
    {
      kind: 'p',
      text: 'Los pagos se procesan bajo entornos seguros mediante la pasarela externa Mercado Pago y/o los medios que se indiquen en el checkout. {businessName} no almacena, no accede ni procesa datos financieros, bancarios ni de tarjetas, que son administrados directamente por el procesador de pagos bajo sus propias políticas de seguridad. La operación puede estar sujeta a validaciones antifraude y aprobación de la entidad emisora o del procesador.',
    },
    { kind: 'h2', text: 'Facturación' },
    {
      kind: 'p',
      text: 'La factura o comprobante correspondiente será emitido conforme la normativa fiscal vigente con los datos suministrados por el cliente al momento de la compra y enviado a su correo electrónico. Es responsabilidad del cliente verificar la exactitud de dichos datos antes de confirmar la operación.',
    },
    { kind: 'h2', text: 'Envíos y entregas' },
    {
      kind: 'p',
      text: 'Los envíos se realizan dentro del territorio de la República Argentina a través de [OPERADOR LOGÍSTICO / MODALIDAD]. Los plazos de entrega son estimados, se computan en días hábiles desde la acreditación del pago y se informan de buena fe; podrán verse alterados por razones ajenas a {businessName}. Es responsabilidad del comprador ingresar el domicilio de envío de forma precisa y exacta.',
    },
    { kind: 'h2', text: 'Garantía legal y reclamos' },
    {
      kind: 'p',
      text: 'Los productos cuentan con las garantías legales que resulten aplicables conforme la Ley 24.240 [VALIDAR CON ABOGADO/A: alcance y plazo de la garantía legal para el rubro — una fuente indica 6 meses para productos nuevos, art. 11 LDC]. Ante defectos o fallas, los costos de traslado y reemplazo corren por cuenta del vendedor. Para consultas, reclamos o gestiones post venta, el usuario podrá comunicarse con {supportArea} a través de {email}, {phone} y/o [FORMULARIO/LINK], en el horario de {hours}.',
    },
    { kind: 'h2', text: 'Derecho de arrepentimiento' },
    {
      kind: 'p',
      text: 'En las compras realizadas a distancia, el consumidor tiene el derecho irrenunciable de revocar la aceptación dentro de los diez (10) días corridos contados desde la entrega del producto o la celebración del contrato, lo que ocurra último (Ley 24.240, CCCN y Disposición 954/2025). {businessName} pone a disposición en su sitio, visible desde el primer acceso, un enlace denominado "BOTÓN DE ARREPENTIMIENTO" para facilitar el ejercicio de ese derecho, sin necesidad de registración previa. Las condiciones completas están detalladas en la Política de Cambios, Devoluciones y Arrepentimiento, que forma parte integrante de estos Términos y Condiciones.',
    },
    { kind: 'h2', text: 'Uso del sitio' },
    {
      kind: 'p',
      text: 'El usuario se compromete a utilizar el sitio de manera lícita, diligente y conforme a estos Términos y Condiciones. Queda prohibido realizar conductas que afecten el funcionamiento del sitio, intenten acceder a información reservada, introduzcan malware o vulneren derechos de terceros.',
    },
    { kind: 'h2', text: 'Propiedad intelectual' },
    {
      kind: 'p',
      text: 'Todos los contenidos del sitio (textos, imágenes, diseños, logos, marcas, bases de datos y software) son de titularidad de {businessName} o de sus respectivos titulares y están protegidos por la normativa aplicable. Su reproducción o uso no autorizado está prohibido.',
    },
    { kind: 'h2', text: 'Protección de datos personales' },
    {
      kind: 'p',
      text: 'El tratamiento de los datos personales del usuario se rige por la Política de Privacidad publicada en el sitio.',
    },
    { kind: 'h2', text: 'Modificaciones' },
    {
      kind: 'p',
      text: '{businessName} podrá modificar estos Términos y Condiciones para adecuarlos a cambios normativos, operativos o comerciales. Las modificaciones regirán desde su publicación y no afectarán operaciones ya perfeccionadas.',
    },
    { kind: 'h2', text: 'Jurisdicción y ley aplicable' },
    {
      kind: 'p',
      text: 'Estos Términos y Condiciones se rigen e interpretan de conformidad con la normativa argentina de defensa del consumidor. Nada de lo aquí dispuesto limita los derechos del consumidor ni altera la competencia que resulte aplicable conforme la ley; ante cualquier conflicto se privilegiarán las vías administrativas y judiciales previstas por la normativa de consumo vigente, correspondientes al domicilio real del consumidor.',
    },
    { kind: 'h2', text: 'Datos del proveedor' },
    {
      kind: 'ul',
      items: [
        '{businessName}',
        'CUIT: {taxId}',
        'Domicilio: {address}',
        'Email: {email}',
        'Teléfono/WhatsApp: {phone}',
        'Horario de atención: {hours}',
        'Área responsable de atención al cliente: {supportArea}',
      ],
    },
  ],
  lawyerNotes: [
    'Política de stock y cancelaciones; formulación del perfeccionamiento contractual.',
    'Cláusula de garantía legal (plazo y alcance para el rubro — dato single-source, ver síntesis D9).',
    'Límites operativos de entrega y responsabilidad por demoras logísticas (sin caer en limitación de responsabilidad ineficaz frente al consumidor).',
    'Redacción final de jurisdicción y resolución de conflictos (COPREC disuelto según una fuente — ver síntesis D5; no citarlo).',
    'Propiedad intelectual y cláusulas sobre fraude/seguridad.',
    'Si corresponde comprometer un horario mínimo de atención (ver síntesis D6).',
  ],
};

const PRIVACIDAD: LegalDoc = {
  slug: 'privacidad',
  title: 'Política de Privacidad',
  seoDescription:
    'Política de privacidad de la tienda: qué datos recolectamos, para qué los usamos, con quién los compartimos y cómo ejercer tus derechos (Ley 25.326).',
  intro:
    'En {businessName} nos comprometemos a tratar tus datos personales de manera lícita, segura y transparente, de acuerdo con la Ley 25.326 de Protección de Datos Personales y su normativa complementaria.',
  blocks: [
    { kind: 'h2', text: 'Responsable del tratamiento' },
    {
      kind: 'p',
      text: 'El responsable de la base de datos es {businessName}, CUIT {taxId}, con domicilio en {address}, correo electrónico {privacyEmail} y teléfono {phone}. Número de registro del responsable y/o de la base ante la AAIP: [NÚMERO DE REGISTRO, CUANDO SE OBTENGA].',
    },
    { kind: 'h2', text: 'Qué datos recolectamos' },
    {
      kind: 'p',
      text: 'Para procesar tus compras y realizar las entregas recolectamos y tratamos las siguientes categorías de datos:',
    },
    {
      kind: 'ul',
      items: [
        'datos identificatorios, como nombre y apellido, y DNI o CUIT cuando sean necesarios para la facturación;',
        'datos de contacto, como email y teléfono;',
        'datos de envío y facturación, como domicilio (calle, número, piso/depto, localidad, código postal, provincia);',
        'datos de compra, pedidos, reclamos y devoluciones;',
        'datos técnicos de navegación, dispositivo, IP, logs y cookies, en los términos explicados más abajo.',
      ],
    },
    {
      kind: 'p',
      text: 'El sitio no solicita ni almacena datos sensibles (opiniones políticas, creencias religiosas, datos de salud) ni datos financieros de tarjetas de crédito o débito: el pago se procesa exclusivamente en la plataforma segura de Mercado Pago.',
    },
    { kind: 'h2', text: 'Para qué usamos tus datos' },
    { kind: 'p', text: 'Tratamos tus datos para:' },
    {
      kind: 'ul',
      items: [
        'gestionar tu registro y/o tu compra en el sitio;',
        'procesar pagos;',
        'coordinar envíos y entregas;',
        'emitir comprobantes y cumplir la facturación exigida por la autoridad fiscal;',
        'responder consultas, reclamos y gestiones post venta (incluido el arrepentimiento);',
        'prevenir fraude y reforzar la seguridad de la operatoria;',
        'cumplir obligaciones legales, fiscales y regulatorias;',
        'enviarte comunicaciones promocionales, únicamente cuando hayas prestado tu consentimiento (suscripción voluntaria, sin casillas premarcadas).',
      ],
    },
    { kind: 'h2', text: 'Base legal del tratamiento' },
    {
      kind: 'p',
      text: 'Cuando el tratamiento es necesario para gestionar la compra, el pago, la entrega, la facturación, la atención post venta o el cumplimiento de obligaciones legales, la base legal es la relación contractual y/o la obligación legal aplicable. Cuando el tratamiento no es necesario para ejecutar la compra —por ejemplo, newsletters o comunicaciones promocionales—, la base legal es tu consentimiento.',
    },
    { kind: 'h2', text: 'Destinatarios y terceros que intervienen' },
    {
      kind: 'p',
      text: 'Tus datos podrán ser compartidos, en la medida necesaria para cumplir las finalidades indicadas, con:',
    },
    {
      kind: 'ul',
      items: [
        'procesadores de pago: Mercado Pago (Mercado Libre S.R.L.), para la tramitación segura del cobro;',
        'operadores logísticos, correos y transportistas: [NOMBRE DE LA EMPRESA DE CORREO/LOGÍSTICA], para el despacho al domicilio declarado;',
        'proveedores tecnológicos de hosting, almacenamiento, soporte, mailing, analítica y seguridad;',
        'estudios contables, asesores y proveedores administrativos bajo deber de confidencialidad;',
        'organismos públicos o autoridades administrativas/judiciales cuando exista obligación legal.',
      ],
    },
    { kind: 'h2', text: 'Transferencias internacionales' },
    {
      kind: 'p',
      text: 'Parte de nuestra infraestructura tecnológica está alojada en la nube por el proveedor [NOMBRE DEL PROVEEDOR DE HOSTING], con servidores en [PAÍS], lo que configura una transferencia internacional de datos en los términos de la Ley 25.326. {businessName} procura que dicha transferencia se realice de conformidad con la normativa aplicable, hacia países con protección adecuada o mediante los mecanismos y salvaguardas admitidos por la autoridad de control. Países/proveedores relevantes: [DETALLAR: EE.UU., UE, ETC.]. Salvaguardas utilizadas: [CONSENTIMIENTO / CLÁUSULAS CONTRACTUALES / PROVEEDOR EN PAÍS ADECUADO / OTRA].',
    },
    { kind: 'h2', text: 'Conservación' },
    {
      kind: 'p',
      text: 'Conservaremos los datos personales durante el tiempo necesario para cumplir la finalidad para la cual fueron recolectados, atender reclamos o cumplir obligaciones legales, fiscales, contables o regulatorias. Luego serán eliminados, anonimizados o bloqueados, según corresponda.',
    },
    { kind: 'h2', text: 'Seguridad y confidencialidad' },
    {
      kind: 'p',
      text: 'Adoptamos medidas técnicas y organizativas razonables para proteger los datos personales contra acceso no autorizado, pérdida, alteración o divulgación indebida. El acceso interno a los datos se limita a personas y proveedores que necesitan conocerlos para cumplir sus funciones.',
    },
    { kind: 'h2', text: 'Derechos del titular' },
    {
      kind: 'p',
      text: 'Como titular de los datos podés ejercer en forma gratuita los derechos de acceso, rectificación, actualización, supresión y confidencialidad en los términos de la Ley 25.326 (el acceso, a intervalos no inferiores a seis meses, salvo interés legítimo). Para hacerlo, escribí a {privacyEmail} o comunicate por [CANAL ALTERNATIVO]. Podremos requerir información razonable para verificar tu identidad antes de procesar el pedido. Los pedidos de acceso se responden dentro de los diez (10) días corridos; los de rectificación, actualización o supresión, dentro de los cinco (5) días hábiles de recibida la solicitud.',
    },
    { kind: 'h2', text: 'Comunicaciones promocionales' },
    {
      kind: 'p',
      text: 'Si recibís comunicaciones promocionales nuestras, podés solicitar en cualquier momento el retiro o bloqueo de tus datos de nuestras bases con fines publicitarios, a través de [EMAIL / LINK DE BAJA / MECANISMO].',
    },
    { kind: 'h2', text: 'Cookies y tecnologías similares' },
    {
      kind: 'p',
      text: 'Este sitio puede utilizar cookies y tecnologías similares con fines técnicos, de seguridad, analíticos y/o publicitarios. Las cookies estrictamente necesarias son indispensables para el funcionamiento del sitio. Si utilizamos cookies analíticas o publicitarias que permitan identificar, perfilar o compartir datos con terceros, informaremos esa circunstancia y, cuando corresponda, pondremos a disposición mecanismos de elección y gestión. Podés configurar tu navegador para rechazar o eliminar cookies, aunque eso podría afectar funcionalidades del sitio.',
    },
    { kind: 'h2', text: 'Menores de edad' },
    {
      kind: 'p',
      text: 'El sitio no está dirigido a menores de edad no emancipados para realizar compras sin la intervención de su representante legal.',
    },
    { kind: 'h2', text: 'Cambios en esta política' },
    {
      kind: 'p',
      text: 'Podemos actualizar esta Política de Privacidad para reflejar cambios normativos, tecnológicos o de operación. La versión vigente será siempre la publicada en el sitio. Última actualización: [FECHA DE PUBLICACIÓN].',
    },
    { kind: 'h2', text: 'Leyenda del órgano de control (obligatoria)' },
    {
      kind: 'p',
      text: 'El titular de los datos personales podrá solicitar el acceso, rectificación, actualización o supresión de sus datos conforme la Ley 25.326. La Agencia de Acceso a la Información Pública, en su carácter de órgano de control de la Ley 25.326, tiene la atribución de atender las denuncias y reclamos que interpongan quienes resulten afectados en sus derechos por incumplimiento de las normas vigentes en materia de protección de datos personales.',
    },
  ],
  lawyerNotes: [
    'Base legal exacta del marketing y redacción del opt-in (ver síntesis D10).',
    'Mapeo completo de terceros (Mercado Pago, logística, hosting, mailing, analytics) y su calificación como encargados o destinatarios.',
    'Transferencias internacionales: Render/Supabase/Cloudflare en EE.UU. (país no listado como "adecuado") → salvaguarda concreta a declarar.',
    'Plazo de retención por categoría de dato.',
    'Tratamiento de cookies/analytics según el stack real del sitio.',
    'Texto final de la leyenda AAIP según la operación concreta.',
  ],
};

const DEVOLUCIONES: LegalDoc = {
  slug: 'devoluciones',
  title: 'Política de Cambios, Devoluciones y Derecho de Arrepentimiento',
  seoDescription:
    'Política de cambios, devoluciones y derecho de arrepentimiento: cómo ejercer el arrepentimiento legal de 10 días, devoluciones por falla y cambios comerciales.',
  intro:
    'En {businessName} queremos que compres con información clara. Esta política distingue tres situaciones diferentes: (1) el derecho legal de arrepentimiento en compras a distancia; (2) las devoluciones por producto defectuoso, dañado o no conforme; (3) los cambios comerciales que voluntariamente ofrece {businessName}.',
  blocks: [
    { kind: 'h2', text: '1. Derecho de arrepentimiento (revocación legal)' },
    {
      kind: 'p',
      text: 'Si realizaste una compra en nuestro sitio, podés revocar la aceptación dentro del plazo legal de diez (10) días corridos contados desde la entrega del producto o desde la celebración del contrato, lo que ocurra último, sin necesidad de expresar causa y sin penalización alguna. Este derecho es irrenunciable.',
    },
    { kind: 'h2', text: 'Cómo ejercerlo' },
    {
      kind: 'p',
      text: 'Tenés disponible en el sitio, visible desde el primer acceso, el enlace denominado "BOTÓN DE ARREPENTIMIENTO":',
    },
    {
      kind: 'ol',
      items: [
        'Ingresá a [URL DE LA HOME] y accedé al enlace "BOTÓN DE ARREPENTIMIENTO".',
        'Completá el formulario con los datos de tu compra: correo electrónico utilizado en la compra y número de pedido [OPCIONAL: nombre completo y teléfono de contacto].',
        'Enviá la solicitud.',
      ],
    },
    {
      kind: 'p',
      text: 'No te vamos a exigir registración previa ni inicio de sesión. En caso de ser necesario para proteger tu identidad y seguridad, podremos solicitarte verificaciones razonables y habituales (por ejemplo, un código de validación enviado al correo o teléfono registrado en la compra), exclusivamente con ese fin y sin que ello demore la gestión (Disposición 3/2026).',
    },
    { kind: 'h2', text: 'Constancia del pedido' },
    {
      kind: 'p',
      text: 'Dentro de las veinticuatro (24) horas de recibida la solicitud, te informaremos por el mismo medio un código de identificación o registración del trámite.',
    },
    { kind: 'h2', text: 'Costo de devolución' },
    {
      kind: 'p',
      text: 'El ejercicio del derecho de arrepentimiento no tiene costo para vos. Los gastos de devolución quedan a cargo de {businessName}: te enviaremos una etiqueta prepaga para despachar el producto en la sucursal de correo más cercana, o coordinaremos el retiro por tu domicilio. Solo te pedimos poner el producto a disposición en condiciones razonablemente compatibles con una revisión normal del bien.',
    },
    { kind: 'h2', text: 'Reintegro del dinero' },
    {
      kind: 'p',
      text: 'Una vez registrada y procesada la revocación, el reintegro será gestionado a través del mismo medio de pago utilizado en la compra o por otro medio acordado, dentro del plazo operativo de [X DÍAS HÁBILES], sin perjuicio de los tiempos propios de acreditación de la entidad emisora o del procesador de pagos.',
    },
    { kind: 'h2', text: 'Supuestos en los que el arrepentimiento puede no corresponder' },
    {
      kind: 'p',
      text: 'Conforme la normativa vigente, el derecho de arrepentimiento puede no resultar aplicable, salvo pacto en contrario, en los casos legalmente exceptuados, como:',
    },
    {
      kind: 'ul',
      items: [
        'productos confeccionados conforme a especificaciones particulares del cliente o claramente personalizados;',
        'productos que por su naturaleza no puedan ser devueltos o puedan deteriorarse con rapidez;',
        'productos perecederos;',
        'otros supuestos legalmente exceptuados por la normativa vigente.',
      ],
    },
    {
      kind: 'p',
      text: '[VALIDAR CON ABOGADO/A: si se puede exigir producto sin uso / empaque original — las fuentes se contradicen en este punto (ver síntesis, discrepancia D3); la redacción de esta plantilla adopta el criterio prudente de no condicionar el ejercicio del derecho.]',
    },
    { kind: 'h2', text: '2. Productos defectuosos, dañados o no conformes (garantía)' },
    {
      kind: 'p',
      text: 'Si el producto recibido presenta fallas, roturas, daños de transporte, faltantes o no coincide con lo comprado, escribinos a {email} o contactanos por [WHATSAPP / TELÉFONO / FORMULARIO] dentro de [PLAZO INTERNO RAZONABLE], adjuntando número de pedido, descripción del inconveniente y, si es posible, fotos. {businessName} coordinará la solución que corresponda según la normativa aplicable: reparación sin cargo, cambio por un producto nuevo, retiro, reposición o devolución del dinero. En estos supuestos, la totalidad de los costos logísticos corre por cuenta del vendedor. [VALIDAR CON ABOGADO/A: plazo y alcance de la garantía legal para el rubro — una fuente indica 6 meses para productos nuevos.]',
    },
    { kind: 'h2', text: '3. Cambios comerciales (por decisión del comprador)' },
    {
      kind: 'p',
      text: 'Además de tus derechos legales, {businessName} ofrece la siguiente política comercial de cambios (por talle, color u otra variante):',
    },
    {
      kind: 'ul',
      items: [
        'plazo: [X DÍAS] desde la recepción;',
        'condición: producto sin uso, con etiquetas, accesorios y embalaje original, salvo que exista falla o no conformidad;',
        'presentar número de pedido y comprobante;',
        'canales habilitados: [EMAIL / WHATSAPP / DOMICILIO / CORREO];',
        'costos logísticos del cambio comercial: [A CARGO DE QUIÉN — DEFINIR].',
      ],
    },
    {
      kind: 'p',
      text: 'Esta política comercial es adicional y no limita ni reemplaza los derechos legales del consumidor (arrepentimiento y garantía).',
    },
    { kind: 'h2', text: 'Canales de atención' },
    {
      kind: 'ul',
      items: [
        'Área responsable del servicio de atención al cliente: {supportArea}',
        'Email: {email}',
        'Teléfono/WhatsApp: {phone}',
        'Formulario: [LINK]',
        'Horario de atención: {hours}',
      ],
    },
  ],
  lawyerNotes: [
    'Lista de excepciones al arrepentimiento según el rubro real (indumentaria/merch) y la cláusula de estado del producto (discrepancia D3 de la síntesis).',
    'Plazo de reintegro comprometido.',
    'Condiciones y costos del cambio comercial (quién paga el flete del cambio voluntario sí es definible comercialmente, a diferencia del arrepentimiento).',
    'Redacción sobre productos usados/consumidos e higiene, si aplica.',
    'Plazo y alcance de la garantía legal (dato single-source, ver síntesis D9).',
  ],
};

/** Los tres documentos legales, indexados por su slug de ruta. */
export const LEGAL_DOCS: Readonly<Record<string, LegalDoc>> = {
  terminos: TERMINOS,
  privacidad: PRIVACIDAD,
  devoluciones: DEVOLUCIONES,
};
